import { useEffect, useMemo, useReducer, useRef, useState } from "react"
import MainStore from "./MainStore"
import { useNavigate, useParams } from 'react-router-dom';
import useDataEvent from "./CustomHook"
import { Table } from "./Table"
import { Temporal } from "@js-temporal/polyfill"
import { EyeIcon, PlayIcon, Cog8ToothIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Badge } from "./@components/badge"
import { Avatar, AvatarGroup, Button, Card, CardHeader, Modal, ModalBody, ModalContent, ModalHeader, Spinner, Tooltip } from "@heroui/react"
import { Icon } from "@iconify/react/dist/iconify.js"
import FlowInstanceEditor from "./FlowInstanceEditor"
import useWorkspaceBranding from "./hooks/useWorkspaceBranding"
import Popup from "./Popup"
import ConfirmationPopup from "./ConfirmationPopup"
import WorkflowOverview from "./WorkflowOverview"
import clsx from "clsx";
import { classNames } from "./SharedTransforms";
import FlowInstanceOutput from "./FlowInstanceOutput";
import { createPptx } from "./PptHelper";
import { buildLatestReplicaMap, compareReplicaToPublished, getFlowSourceId, pickNewestReplica } from "./utils/flowRevisions";

const EXPORT_REF_WAIT_TIMEOUT_MS = 20000

function MultiSetupModal({ flow, isOpen, onClose, accentButtonClassName }) {
    const [rows, setRows] = useState([{ id: "row-1", title: "", data: {} }])

    useEffect(() => {
        if (isOpen) {
            setRows([{ id: "row-1", title: "", data: {} }])
        }
    }, [isOpen, flow?.id])

    if (!flow) return null

    const pins = flow.getConfig?.inputPins ?? {}
    const gridPins = Object.entries(pins).filter(([, info]) => !info.split)

    function addRow() {
        setRows(prev => [...prev, { id: `row-${prev.length + 1}-${prev.length}`, title: "", data: {} }])
    }
    function removeRow(rowId) {
        setRows(prev => prev.length > 1 ? prev.filter(r => r.id !== rowId) : prev)
    }
    function updateTitle(rowId, value) {
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, title: value } : r))
    }
    function updateCell(rowId, key, value) {
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, data: { ...r.data, [key]: value } } : r))
    }

    function handlePaste(e, rowId, startColIndex) {
        // startColIndex: -1 = title column, 0+ = gridPins index
        const text = e.clipboardData.getData("text/plain")
        const lines = text.split(/\r?\n/).filter(l => l !== "")
        if (lines.length === 0) return
        if (lines.length === 1 && !lines[0].includes("|")) return

        e.preventDefault()
        setRows(prev => {
            const startRowIndex = prev.findIndex(r => r.id === rowId)
            if (startRowIndex === -1) return prev

            const next = prev.map(r => ({ ...r, data: { ...r.data } }))
            lines.forEach((line, lineIdx) => {
                const parts = line.split("|")
                const targetRowIndex = startRowIndex + lineIdx
                if (targetRowIndex >= next.length) {
                    next.push({ id: `row-paste-${next.length}`, title: "", data: {} })
                }
                parts.forEach((part, partIdx) => {
                    const targetColIndex = startColIndex + partIdx
                    if (targetColIndex === -1) {
                        next[targetRowIndex].title = part.trim()
                    } else if (targetColIndex >= 0 && targetColIndex < gridPins.length) {
                        const [key] = gridPins[targetColIndex]
                        next[targetRowIndex].data[key] = part.trim()
                    }
                })
            })
            return next
        })
    }

    function handleCreate() {
        const store = MainStore()
        rows.forEach(row => {
            const trimmedTitle = row.title?.trim()
            const actionParams = { data: trimmedTitle ? { title: trimmedTitle, ...row.data } : row.data }
            store.doPrimitiveAction(flow, "create_flowinstance", actionParams, async (data) => {
                const newId = data?._id
                if (!newId) return
                const flowInstance = await store.waitForPrimitive(newId)
                if (!flowInstance) return
                store.doPrimitiveAction(flowInstance, "continue_flow_instance", {
                    organizationId: store.activeOrganization?.id,
                })
            })
        })
        onClose()
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
            <ModalContent>
                <ModalHeader className="flex items-center gap-3">
                    <Icon icon="solar:list-linear" className="h-5 w-5 text-slate-400" />
                    <span>Create multiple — {flow.title}</span>
                </ModalHeader>
                <ModalBody className="pb-6">
                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 min-w-[180px]">
                                            Title <span className="ml-1 font-normal normal-case text-slate-400">optional</span>
                                        </th>
                                        <th className="w-10 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">#</th>
                                        {gridPins.map(([key, info]) => (
                                            <th key={key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 min-w-[180px]">
                                                {info.name}
                                                {info.optional === false || info.required === true
                                                    ? <span className="text-rose-500 ml-1">*</span>
                                                    : <span className="ml-1 font-normal normal-case text-slate-400">optional</span>}
                                            </th>
                                        ))}
                                        <th className="w-12" />
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {rows.map((row, idx) => (
                                        <tr key={row.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-2 py-2">
                                                <input
                                                    type="text"
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-50"
                                                    placeholder="Title"
                                                    value={row.title ?? ""}
                                                    onChange={e => updateTitle(row.id, e.target.value)}
                                                    onPaste={e => handlePaste(e, row.id, -1)}
                                                />
                                            </td>
                                            <td className="px-3 py-2.5 text-center text-xs text-slate-400">{idx + 1}</td>
                                            {gridPins.map(([key, info], colIdx) => {
                                                const isLong = info.format === "long_string" || (Array.isArray(info.types) && info.types.includes("long_string"))
                                                return (
                                                    <td key={key} className="px-2 py-2">
                                                        {isLong ? (
                                                            <textarea
                                                                rows={2}
                                                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-50 resize-none"
                                                                placeholder={info.placeholder ?? info.example ?? info.name}
                                                                value={row.data[key] ?? ""}
                                                                onChange={e => updateCell(row.id, key, e.target.value)}
                                                                onPaste={e => handlePaste(e, row.id, colIdx)}
                                                            />
                                                        ) : (
                                                            <input
                                                                type={info.format === "url" || (Array.isArray(info.types) && info.types.includes("url")) ? "url" : "text"}
                                                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-50"
                                                                placeholder={info.placeholder ?? info.example ?? info.name}
                                                                value={row.data[key] ?? ""}
                                                                onChange={e => updateCell(row.id, key, e.target.value)}
                                                                onPaste={e => handlePaste(e, row.id, colIdx)}
                                                            />
                                                        )}
                                                    </td>
                                                )
                                            })}
                                            <td className="px-3 py-2 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => removeRow(row.id)}
                                                    disabled={rows.length === 1}
                                                    className="inline-flex items-center justify-center h-7 w-7 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                                    aria-label="Remove row"
                                                >
                                                    <Icon icon="solar:trash-bin-2-linear" className="h-3.5 w-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                            <Button
                                size="sm"
                                variant="flat"
                                startContent={<Icon icon="solar:add-circle-linear" className="h-4 w-4" />}
                                onPress={addRow}
                            >
                                Add row
                            </Button>
                            <span className="text-xs text-slate-400">
                                {rows.length} instance{rows.length !== 1 ? "s" : ""} to create
                            </span>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="light" onPress={onClose}>Cancel</Button>
                        <Button
                            className={accentButtonClassName}
                            startContent={<Icon icon="solar:play-circle-linear" className="h-4 w-4" />}
                            onPress={handleCreate}
                        >
                            Create {rows.length} instance{rows.length !== 1 ? "s" : ""}
                        </Button>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}
const EXPORT_RENDER_READY_TIMEOUT_MS = 18000
const EXPORT_RENDER_SETTLE_MS = 650

export default function WorkflowDashboard(){
    const navigate = useNavigate()        
    const [showNew, setShowNew] = useState(false)
    const [editFlowInstance, setEditFlowInstance] = useState(false)
    const [selectedWorkflowOverview, setSelectedWorkflowOverview] = useState(undefined)
    const [preselectedWorkflow, setPreselectedWorkflow] = useState(undefined)
    const [multiSetupFlow, setMultiSetupFlow] = useState(undefined)
    const [selectedFlowInstanceIds, setSelectedFlowInstanceIds] = useState(new Set())
    const [isDownloadingPptx, setIsDownloadingPptx] = useState(false)
    const [exportingFlowInstancePrimitive, setExportingFlowInstancePrimitive] = useState(undefined)
    const [exportRenderKey, setExportRenderKey] = useState(0)
    const [frozenFlowInstanceInfo, setFrozenFlowInstanceInfo] = useState(undefined)
    const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(false)
    const [flowInstancePendingDelete, setFlowInstancePendingDelete] = useState(undefined)
    const flowInstanceTableOrderRef = useRef([])
    const [count, forceUpdate] = useReducer( (x)=>x+1, 0)
    const [workspaceTemplates, setWorkspaceTemplates] = useState([])
    const isMountedRef = useRef(true)
    const workspaceTemplateCache = useMemo(()=>new Map(), [])
    const flowOutputRefs = useRef(new Map())
    const singleExportFlowOutputRef = useRef(undefined)

    const {id} = useParams()

    useEffect(()=>{
        isMountedRef.current = true
        return ()=>{
            isMountedRef.current = false
        }
    }, [])

    useEffect(()=>{
        let cancelled = false
        const loadWorkflowWorkspace = async ()=>{
            if( !id ){
                const currentWorkspaceId = MainStore().activeWorkspaceId
                if( currentWorkspaceId ){
                    navigate(`/workflows/${currentWorkspaceId}`, {replace: true})
                }
                return
            }
            setIsWorkspaceLoading(true)
            try{
                await MainStore().loadActiveWorkspace(id, {mode: "workflows", background: true})
            }finally{
                if( !cancelled ){
                    setIsWorkspaceLoading(false)
                }
            }
        }
        loadWorkflowWorkspace()
        return ()=>{
            cancelled = true
        }
    }, [id, navigate])
    


    const activeWorkspaceId = MainStore().activeWorkspaceId

    const workspaces = MainStore().workspaces?.() ?? []
    const activeWorkspace = useMemo(()=>{
        if( !activeWorkspaceId ){
            return undefined
        }
        return workspaces.find((workspace)=>{
            const matchId = workspace.id ?? workspace._id
            return `${matchId}` === `${activeWorkspaceId}`
        })
    }, [workspaces, activeWorkspaceId])
    const {
        workspaceGradient,
        accentColor,
        accentBorderClassName,
        accentBaseClassName,
        accentPanelClassName,
        accentButtonClassName,
        accentTextClassName,
        accentSoftTextClassName,
        accentTrackClassName,
        accentIndicatorClassName,
        accentContrastClassName,
        secondaryButtonClassName,
        workspaceLogo,
    } = useWorkspaceBranding(activeWorkspace)
    const workspaceUsersAll = useMemo(()=>{
        if( !activeWorkspaceId ){
            return []
        }
        const users = typeof MainStore().users === "function" ? MainStore().users() : []
        if( !Array.isArray(users) ){
            return []
        }
        return users.filter(user=>{
            const workspacesForUser = user.workspaces ?? []
            return workspacesForUser.map((id)=>`${id}`).includes(`${activeWorkspaceId}`)
        })
    }, [activeWorkspaceId, count])
    const displayedWorkspaceUsers = workspaceUsersAll.slice(0,4)
    const remainingWorkspaceUsers = Math.max(0, workspaceUsersAll.length - displayedWorkspaceUsers.length)
    const workspaceTitle = activeWorkspace?.title ?? "Workflow Dashboard"
    const workspaceSubtitle = activeWorkspace?.description ?? "Monitor in-flight research and launch new workflows for your team."

    const filterForWorksapce = (array)=>{
        if( activeWorkspaceId === undefined ){
            return array
        }
        const targetWorkspaceId = `${activeWorkspaceId}`
        return array.filter((d)=>{
            if( d?.workspaceId === undefined || d?.workspaceId === null ){
                return true
            }
            return `${d.workspaceId}` === targetWorkspaceId
        })
    }

    useEffect(()=>{
        if( !MainStore().homescreenReady){
            MainStore().loadHomeScreenPrimitives()
        }
    }, [MainStore().homescreenReady])
    
    const workflows = filterForWorksapce(MainStore().primitives().filter((p)=>p.type==="flow" && !p.inFlow))

    useEffect(()=>{
        if(!activeWorkspaceId){
            if(isMountedRef.current){
                setWorkspaceTemplates([])
            }
            return
        }
        const store = MainStore()
        const cacheKey = `${activeWorkspaceId}`
        const cached = workspaceTemplateCache.get(cacheKey)
        if(Array.isArray(cached)){
            setWorkspaceTemplates(cached)
            return
        }
        store.fetchWorkspaceTemplates(activeWorkspaceId)
            .then((templates)=>{
                if(!isMountedRef.current || `${MainStore().activeWorkspaceId}` !== cacheKey){
                    return
                }
                const normalized = Array.isArray(templates) ? templates : []
                workspaceTemplateCache.set(cacheKey, normalized)
                setWorkspaceTemplates(normalized)
            })
            .catch((error)=>{
                if(isMountedRef.current && `${MainStore().activeWorkspaceId}` === cacheKey){
                    console.error("Failed to load permitted templates", error)
                    if(!workspaceTemplateCache.has(cacheKey)){
                        setWorkspaceTemplates([])
                    }
                }
            })
    }, [activeWorkspaceId, workspaceTemplateCache])

    useDataEvent('new_primitive delete_primitive relationship_update', undefined, ()=>{
        forceUpdate()
    })
    useDataEvent('workspace_updated workspace_added workspace_removed', activeWorkspaceId ? [activeWorkspaceId] : undefined, ()=>{
        if(activeWorkspaceId){
            const cacheKey = `${activeWorkspaceId}`
            MainStore()
                .fetchWorkspaceTemplates(activeWorkspaceId)
                .then((templates)=>{
                    if(!isMountedRef.current || `${MainStore().activeWorkspaceId}` !== cacheKey){
                        return
                    }
                    const normalized = Array.isArray(templates) ? templates : []
                    workspaceTemplateCache.set(cacheKey, normalized)
                    setWorkspaceTemplates(normalized)
                })
                .catch((error)=>{
                    if(isMountedRef.current && `${MainStore().activeWorkspaceId}` === cacheKey){
                        console.error("Failed to refresh workspace templates", error)
                        if(!workspaceTemplateCache.has(cacheKey)){
                            setWorkspaceTemplates([])
                        }
                    }
                })
        }
        forceUpdate()
    })

    const safeInstant = (value)=>{
        if( !value || typeof value !== "string" ){
            return undefined
        }
        try{
            return Temporal.Instant.from(value)
        }catch(err){
            console.warn("Unable to parse timestamp", value, err)
            return undefined
        }
    }

    const formatNumber = (value)=>{
        if( value === undefined || value === null ){
            return "0"
        }
        const num = Number(value)
        if( Number.isNaN(num) ){
            return "0"
        }
        if( num >= 1000000 ){
            return `${(num/1000000).toFixed(1).replace(/\.0$/,"")}M`
        }
        if( num >= 1000 ){
            return `${(num/1000).toFixed(1).replace(/\.0$/,"")}k`
        }
        return `${Math.round(num)}`
    }

    let steps = [], flowInstances = []
    workflows.forEach(d=>d.primitives.origin.allUniqueItems.forEach(d=>{
        if(d.type === "flowinstance"){
            flowInstances.push(d)
        }else{
            if( d.type !== "view" && d.type !== "page"){
                steps.push(d)
            }
        }
    }))
    const watchIds = [...flowInstances.map(d=>d.id),...steps.map(d=>d.id)]
    useDataEvent("set_field ", watchIds, ()=>{
        forceUpdate()
    })

    const decoratedFlowInstances = useMemo(()=>{
        return flowInstances.map(instance=>{
            const status = instance.processing?.flow?.status ?? "not_started"
            const startedInstant = safeInstant(instance.processing?.flow?.started ?? instance.createdAt)
            const completedInstant = safeInstant(instance.processing?.flow?.completed)
            let statusText = {
                "not_started": "Not Started",
                "running": "Running...",
                "paused": "Paused - waiting for confirmation",
                "complete": completedInstant ? `Completed: ${completedInstant.toZonedDateTimeISO("UTC").toPlainDate().toLocaleString("en-US", {
                    day:   "numeric",
                    month: "long",
                    year:  "numeric"
                  })}` : "Completed",
            }[status] ?? "Not Started"
            const color = {
                "not_started": "default",
                "running": "primary",
                "paused": "warning",
                "complete": "success",
            }[status] ?? "default"

            let progressDetails = []
            if( status === "running"){
                const progressStatus = []
                instance.primitives.origin.map(step=>{
                    let added = false
                    for(const [_, value] of Object.entries(step.processing ?? {})){
                        if( !value ){
                            continue
                        }
                        if( value.progress && typeof value.progress === "string"){
                            progressStatus.push([step, value.progress])
                            added = true
                        }else if( value.message && instance.processing?.flow?.status === "running"){
                            progressStatus.push([step, value.message])
                            added = true
                        }
                    }
                    if( !added && instance.processing?.flow?.status === "running"){
                        progressStatus.push([step,""])
                    }
                })
                if( progressStatus.length > 1 ){
                    statusText = `Running ${progressStatus.length} items...`
                    progressDetails = progressStatus.map(([step, msg])=>{
                        const label = step.getConfig.labelForMap ?? step.configParent?.title ?? `#${step.plainId}`
                        return `${label}${msg ? ` – ${msg}` : ""}`
                    })
                }else if(progressStatus.length === 1){
                    const [step, msg] = progressStatus[0]
                    const label = step.getConfig.labelForMap ?? step.configParent?.title ?? `#${step.plainId}`
                    statusText = `Running ${label}...`
                    if( msg ){
                        progressDetails = [msg]
                    }
                }
            }

            const percentageProgress = instance.processing?.flow?.progress?.percent ?? 0 //instance.percentageProgress
            const parentFlow = instance.origin

            return {
                raw: instance,
                id: instance.id,
                plainId: instance.plainId,
                title: instance.title,
                tags: instance.referenceParameters.tags,
                type: parentFlow?.title ?? "Flow",
                status,
                statusText,
                statusColor: color,
                progressDetails,
                startedInstant,
                completedInstant,
                percentageProgress: typeof percentageProgress === "number" ? Math.max(0, Math.min(percentageProgress <= 1 ? percentageProgress * 100 : percentageProgress, 100)) : undefined,
            }
        })
    }, [flowInstances, count])

    const flowInstanceInfo = decoratedFlowInstances.map(info=>{
        const badgeColorMap = {
            default: "default",
            primary: "blue",
            warning: "warning",
            success: "green"
        }
        const badgeColor = badgeColorMap[info.statusColor] ?? "grey"
        const progressSection = info.status === "complete"
                                    ? <div className="flex space-x-1 place-items-center">
                                        <Icon icon="lucide:circle-check-big" className={`w-4 h-4 ${accentTextClassName}`} /><p className="text-sm">Completed</p>
                                    </div>
                                    : <div className="text-xs space-y-1">
                                            <Badge color={badgeColor}>{info.statusText}</Badge>
                                        </div>
        const relevantInstant = info.completedInstant ?? info.startedInstant
        const dateRun = relevantInstant
            ? relevantInstant.toZonedDateTimeISO("UTC").toPlainDate().toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric"
              })
            : "—"
        const sortTimestamp = relevantInstant ? relevantInstant.epochMilliseconds : 0
        return {
            id: info.id,
            plainId: info.plainId,
            title: info.title,
            tags: info.tags,
            type: info.type,
            dateRun,
            sortTimestamp,
            progressSection,
            primitive: info.raw
        }
    })
    const displayedFlowInstanceInfo = useMemo(()=>{
        const source = (isDownloadingPptx && Array.isArray(frozenFlowInstanceInfo))
            ? frozenFlowInstanceInfo
            : flowInstanceInfo
        return [...source].sort((a, b) => b.sortTimestamp - a.sortTimestamp)
    }, [flowInstanceInfo, isDownloadingPptx, frozenFlowInstanceInfo])
    const selectedFlowInstancesForExport = useMemo(()=>{
        if( selectedFlowInstanceIds.size === 0 ){
            return []
        }
        return decoratedFlowInstances.filter(item=>selectedFlowInstanceIds.has(item.id))
    }, [decoratedFlowInstances, selectedFlowInstanceIds])

    const orderSelectedForExport = ()=>{
        const decoratedById = new Map(decoratedFlowInstances.map(item=>[item.id, item]))
        const ordered = []
        const seen = new Set()
        const tableOrder = flowInstanceTableOrderRef.current?.length
            ? flowInstanceTableOrderRef.current
            : displayedFlowInstanceInfo.map(d=>d.id)
        for(const id of tableOrder){
            if( !selectedFlowInstanceIds.has(id) || seen.has(id) ){
                continue
            }
            const decorated = decoratedById.get(id)
            if( decorated ){
                ordered.push(decorated)
                seen.add(id)
            }
        }
        for(const item of decoratedFlowInstances){
            if( selectedFlowInstanceIds.has(item.id) && !seen.has(item.id) ){
                ordered.push(item)
                seen.add(item.id)
            }
        }
        return ordered
    }

    useEffect(()=>{
        if( isDownloadingPptx ){
            return
        }
        if( selectedFlowInstanceIds.size === 0 ){
            return
        }
        const activeIds = new Set(decoratedFlowInstances.map(item=>item.id))
        setSelectedFlowInstanceIds((prev)=>{
            let changed = false
            const next = new Set()
            prev.forEach((id)=>{
                if( activeIds.has(id) ){
                    next.add(id)
                }else{
                    changed = true
                    flowOutputRefs.current.delete(id)
                }
            })
            return changed ? next : prev
        })
    }, [decoratedFlowInstances, selectedFlowInstanceIds.size, isDownloadingPptx])

    const toggleFlowInstanceSelection = (id, checked)=>{
        if( !id ){
            return
        }
        setSelectedFlowInstanceIds((prev)=>{
            const next = new Set(prev)
            if( checked ){
                next.add(id)
            }else{
                next.delete(id)
                flowOutputRefs.current.delete(id)
            }
            return next
        })
    }

    const clearSelectedFlowInstances = ()=>{
        setSelectedFlowInstanceIds(new Set())
        flowOutputRefs.current.clear()
    }

    const requestDeleteFlowInstance = (item)=>{
        if( !item?.id ){
            return
        }
        setFlowInstancePendingDelete({
            id: item.id,
            plainId: item.plainId,
            title: item.title,
            primitive: item.primitive ?? item,
        })
    }

    const confirmDeleteFlowInstance = async ()=>{
        if( !flowInstancePendingDelete?.id ){
            return false
        }
        try{
            await MainStore().removePrimitive(flowInstancePendingDelete.primitive ?? flowInstancePendingDelete.id)
            setSelectedFlowInstanceIds((prev)=>{
                if( !prev.has(flowInstancePendingDelete.id) ){
                    return prev
                }
                const next = new Set(prev)
                next.delete(flowInstancePendingDelete.id)
                return next
            })
            flowOutputRefs.current.delete(flowInstancePendingDelete.id)
            setFlowInstancePendingDelete(undefined)
            return true
        }catch(err){
            console.error("Failed to delete flow instance", err)
            return false
        }
    }

    const downloadSelectedOutputs = async ()=>{
        if( isDownloadingPptx || selectedFlowInstancesForExport.length === 0 ){
            return
        }
        const orderedForExport = orderSelectedForExport()
        setIsDownloadingPptx(true)
        try{
            const waitForExportRef = async (timeoutMs = EXPORT_REF_WAIT_TIMEOUT_MS)=>{
                const started = Date.now()
                while( Date.now() - started < timeoutMs ){
                    if( singleExportFlowOutputRef.current?.exportToPptx ){
                        return singleExportFlowOutputRef.current
                    }
                    await new Promise((resolve)=>setTimeout(resolve, 50))
                }
                return undefined
            }
            const resolvePrimitiveById = (id)=>{
                return MainStore().primitive(id) ?? MainStore().primitiveByPlain(parseInt(id))
            }
            const normalizeTitle = (item, idx)=>{
                const rawTitle = item?.title ?? item?.raw?.title ?? item?.type ?? `Workflow ${idx + 1}`
                const trimmed = `${rawTitle}`.trim()
                return trimmed.length > 0 ? trimmed : `Workflow ${idx + 1}`
            }
            const selectedTitles = orderedForExport.map(normalizeTitle)
            const buildFileBaseName = ()=>{
                if( selectedTitles.length === 1 ){
                    return selectedTitles[0]
                }
                if( selectedTitles.length === 2 ){
                    return `${selectedTitles[0]} and ${selectedTitles[1]}`
                }
                return `${selectedTitles[0]}, ${selectedTitles[1]} and ${selectedTitles.length - 2} others`
            }
            const sanitizeFileName = (value)=>{
                const sanitized = value
                    .replace(/[\\/:*?"<>|]/g, " ")
                    .replace(/\s+/g, " ")
                    .trim()
                return sanitized.length > 0 ? sanitized : "workflow_outputs"
            }
            const outputFileName = sanitizeFileName(buildFileBaseName())
            const pptx = createPptx()
            let exportedSlides = 0
            const exportQueue = [...orderedForExport]
            setFrozenFlowInstanceInfo(flowInstanceInfo)
            for(const item of exportQueue){
                await MainStore().loadActiveWorkspace(activeWorkspaceId, {mode: "flowinstance", focusId: item.id, background: true})
                const loadedPrimitive = resolvePrimitiveById(item.id)
                if( !loadedPrimitive ){
                    continue
                }
                setExportingFlowInstancePrimitive(loadedPrimitive)
                setExportRenderKey((prev)=>prev + 1)
                const flowRef = await waitForExportRef()
                if( !flowRef?.exportToPptx ){
                    continue
                }
                await flowRef.waitForRenderStable?.({
                    timeoutMs: EXPORT_RENDER_READY_TIMEOUT_MS,
                    settleMs: EXPORT_RENDER_SETTLE_MS
                })
                const thisExported = await flowRef.exportToPptx(pptx)
                exportedSlides += Number(thisExported ?? 0)
                setExportingFlowInstancePrimitive(undefined)
                singleExportFlowOutputRef.current = undefined
            }
            if( exportedSlides > 0 ){
                await pptx.writeFile({ fileName: `${outputFileName}.pptx` })
            }else{
                console.warn("No slides were exported from selected workflow outputs")
            }
        }catch(error){
            console.error("Failed to export selected workflow outputs", error)
        }finally{
            setExportingFlowInstancePrimitive(undefined)
            singleExportFlowOutputRef.current = undefined
            setFrozenFlowInstanceInfo(undefined)
            if( activeWorkspaceId ){
                await MainStore().loadActiveWorkspace(activeWorkspaceId, {mode: "workflows", background: true})
            }
            setIsDownloadingPptx(false)
        }
    }

    const templateSearchBadges = useMemo(()=>{
        const seen = new Set()
        const badges = []
        for (const item of displayedFlowInstanceInfo){
            const template = item.type
            if(!template){
                continue
            }
            if(seen.has(template)){
                continue
            }
            seen.add(template)
            badges.push({
                title: template,
                searchKey: template
            })
        }
        badges.sort((a,b)=>a.title.localeCompare(b.title))
        return badges
    }, [displayedFlowInstanceInfo])

    const monthlyStats = useMemo(()=>{
        const now = Temporal.Now.instant()
        const monthAgo = now.subtract({hours: 30 * 24})
        const recentInstances = decoratedFlowInstances.filter(item=>{
            if( !item.startedInstant ){
                return false
            }
            return Temporal.Instant.compare(item.startedInstant, monthAgo) >= 0
        })

        const workflowsRun = recentInstances.length
        let sourcesProcessed = 0
        let entityProcessed = 0

        recentInstances.forEach(item=>{
            const instance = item.raw
            const searchSteps = instance?.primitives?.origin?.allItems?.filter?.(step=>step.type === "search") ?? []
            searchSteps.forEach(step=>{
                const stats = step.progressStats
                if( stats ){
                    const scanned = Number(stats.totalScanned ?? stats.totalCount ?? 0)
                    if( Number.isFinite(scanned) ){
                        if( step.referenceId === 69){
                            entityProcessed += scanned

                        }else{
                            sourcesProcessed += scanned
                        }
                    }
                }
            })
        })

        return {
            workflowsRun,
            sourcesProcessed,
            entityProcessed,
            teamMembersCount: workspaceUsersAll.length
        }
    }, [decoratedFlowInstances, workspaceUsersAll.length])

    const quickLaunchItems = useMemo(()=>{
        const latestByFlow = new Map()
        decoratedFlowInstances.forEach(item=>{
            const parent = item.raw.origin
            if( !parent ){
                return
            }
            const dedupeKey = getFlowSourceId(parent) ?? parent.id
            const existing = latestByFlow.get(dedupeKey)
            if( !existing ){
                latestByFlow.set(dedupeKey, {
                    flow: parent,
                    instance: item.raw,
                    startedInstant: item.startedInstant
                })
                return
            }

            const newestFlow = pickNewestReplica([existing.flow, parent])
            if( newestFlow?.id !== existing.flow?.id ){
                latestByFlow.set(dedupeKey, {
                    flow: newestFlow,
                    instance: item.raw,
                    startedInstant: item.startedInstant
                })
                return
            }

            if(
                (item.startedInstant && existing.startedInstant && Temporal.Instant.compare(item.startedInstant, existing.startedInstant) > 0)
                || (item.startedInstant && !existing.startedInstant)
            ){
                latestByFlow.set(dedupeKey, {
                    flow: existing.flow,
                    instance: item.raw,
                    startedInstant: item.startedInstant
                })
            }
        })

        const sorted = Array.from(latestByFlow.values()).sort((a,b)=>{
            if( !a.startedInstant || !b.startedInstant ){
                return (b.startedInstant ? 1 : 0) - (a.startedInstant ? 1 : 0)
            }
            return Temporal.Instant.compare(b.startedInstant, a.startedInstant)
        })
        return sorted.slice(0,5)

    }, [decoratedFlowInstances])

    const flowTemplates = useMemo(()=>{
        if(!Array.isArray(workspaceTemplates)){
            return []
        }
        return workspaceTemplates.filter((template)=>template?.type === "flow")
    }, [workspaceTemplates])

    const workspaceFlows = useMemo(()=>workflows, [workflows])

    const {
        importedTemplateFlows,
        templateLibrary,
        customWorkspaceFlows,
        visibleWorkspaceFlows,
    } = useMemo(()=>{
        const templatesById = new Map()
        const customById = new Map()
        flowTemplates.forEach((template)=>{
            if(!template){
                return
            }
            const key = `${template.id ?? template._id ?? ""}`
            if(key){
                templatesById.set(key, template)
            }
        })

        const latestReplicaBySource = buildLatestReplicaMap(workspaceFlows)
        const visibleFlows = workspaceFlows.filter((flow)=>{
            const sourceId = getFlowSourceId(flow)
            if(!sourceId){
                return true
            }
            return latestReplicaBySource.get(sourceId)?.id === flow.id
        })

        const imported = Array.from(latestReplicaBySource.entries())
            .filter(([sourceId])=>templatesById.has(sourceId))
            .map(([, flow])=>flow)

        const usedFlowIds = new Set(imported.map((flow)=>flow?.id ?? flow?._id))
        const custom = visibleFlows.filter((flow)=>{
            const flowId = flow?.id ?? flow?._id
            if( !usedFlowIds.has(flowId) ){
                customById.set( flowId, flow)
                return true
            }
            return false
        })

        const remainingTemplates = flowTemplates.filter((template)=>{
            const key = `${template.id ?? template._id ?? ""}`
            if(!key){
                return false
            }
            const latestReplica = latestReplicaBySource.get(key)
            if(!latestReplica){
                return !customById.has(key)
            }
            return compareReplicaToPublished(latestReplica, template) < 0
        })

        return {
            importedTemplateFlows: imported,
            templateLibrary: remainingTemplates,
            customWorkspaceFlows: custom,
            visibleWorkspaceFlows: visibleFlows,
        }
    }, [flowTemplates, workspaceFlows])

    const workflowCategories = useMemo(()=>{
        const categories = []
        if(customWorkspaceFlows.length > 0){
            categories.push({
                key: "custom",
                title: "Custom Workflows",
                description: "Flows created directly in this workspace",
                items: customWorkspaceFlows.map((flow)=>({kind: "custom", record: flow}))
            })
        }
        if(importedTemplateFlows.length > 0){
            categories.push({
                key: "workspace",
                title: "Workspace Templates",
                description: "Templates already added to this workspace",
                items: importedTemplateFlows.map((flow)=>({kind: "imported", record: flow}))
            })
        }
        if(templateLibrary.length > 0){
            categories.push({
                key: "library",
                title: "Template Library",
                description: "Templates available to import",
                items: templateLibrary.map((template)=>({kind: "template", record: template}))
            })
        }
        return categories
    }, [importedTemplateFlows, templateLibrary, customWorkspaceFlows])

    const handleOpenWorkflowOverview = (flow)=>{
        setSelectedWorkflowOverview(flow)
    }

    const handleCloseWorkflowOverview = ()=>{
        setSelectedWorkflowOverview(undefined)
    }

    const handleRunWorkflow = (flow)=>{
        if(!flow){
            return
        }
        MainStore().doPrimitiveAction(flow, "create_flowinstance")
        handleCloseWorkflowOverview()
    }

    const handleConfigureWorkflow = (flow)=>{
        if(!flow){
            return
        }
        handleCloseWorkflowOverview()
        if(flow.referenceParameters?.allowMultiSetup){
            setMultiSetupFlow(flow)
        }else{
            setPreselectedWorkflow(flow)
            setShowNew(true)
        }
    }

    

    return (
    <div className="relative w-full h-full min-h-0 overflow-hidden">
    <div className="w-full h-full overflow-y-scroll px-4 py-6 max-w-7xl mx-auto space-y-8">
        <section className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-lg">
            <div className="absolute inset-0" style={{ backgroundImage: workspaceGradient, backgroundSize: "cover" }} />
            <div className="absolute inset-0 bg-slate-900/25" />
            <div className="relative flex flex-col gap-6 p-6 lg:p-8 text-white">
                <div className="flex flex-wrap items-start gap-6 justify-between">
                    <div className="flex items-center gap-4">
                        {workspaceLogo}
                        <div className="max-w-2xl">
                            <p className="text-xs uppercase tracking-[0.3em] text-white/70">Welcome back</p>
                            <h1 className="text-2xl lg:text-3xl font-semibold leading-tight">{workspaceTitle}</h1>
                            {workspaceSubtitle && (
                                <p className="text-sm text-white/80 mt-2 line-clamp-2">
                                    {workspaceSubtitle}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            color={accentColor ? "default" : "primary"}
                            variant={accentColor ? "solid" : "shadow"}
                            className={`font-semibold shadow-md hover:shadow-lg ${secondaryButtonClassName}`}
                            onPress={()=>setShowNew(true)}
                            startContent={
                                <Icon className="flex-none text-current" icon="mdi:sparkles-outline" width={16} />
                            }
                        >
                            Choose a flow
                        </Button>
                    </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className={`rounded-2xl border border-white/20 bg-white/10 px-5 py-4 backdrop-blur ${accentColor ? accentContrastClassName : "text-white"}`.trim()}>
                        <p className={`text-xs uppercase tracking-wide ${accentColor ? accentSoftTextClassName : "text-white/70"}`.trim()}>Workflows Run</p>
                        <p className={`mt-2 text-2xl font-semibold ${accentColor ? "" : "text-white"}`.trim()}>{formatNumber(monthlyStats.workflowsRun)}</p>
                        <p className={`text-xs mt-1 ${accentColor ? accentSoftTextClassName : "text-white/70"}`.trim()}>Last 30 days</p>
                    </div>
                    <div className={`rounded-2xl border border-white/20 bg-white/10 px-5 py-4 backdrop-blur ${accentColor ? accentContrastClassName : "text-white"}`.trim()}>
                        <p className={`text-xs uppercase tracking-wide ${accentColor ? accentSoftTextClassName : "text-white/70"}`.trim()}>Companies Analyzed</p>
                        <p className={`mt-2 text-2xl font-semibold ${accentColor ? "" : "text-white"}`.trim()}>{formatNumber(monthlyStats.entityProcessed)}</p>
                        <p className={`text-xs mt-1 ${accentColor ? accentSoftTextClassName : "text-white/70"}`.trim()}>Across workflows</p>
                    </div>
                    <div className={`rounded-2xl border border-white/20 bg-white/10 px-5 py-4 backdrop-blur ${accentColor ? accentContrastClassName : "text-white"}`.trim()}>
                        <p className={`text-xs uppercase tracking-wide ${accentColor ? accentSoftTextClassName : "text-white/70"}`.trim()}>Sources Processed</p>
                        <p className={`mt-2 text-2xl font-semibold ${accentColor ? "" : "text-white"}`.trim()}>{formatNumber(monthlyStats.sourcesProcessed)}</p>
                        <p className={`text-xs mt-1 ${accentColor ? accentSoftTextClassName : "text-white/70"}`.trim()}>Aggregated from searches</p>
                    </div>
                    <div className={`rounded-2xl border border-white/20 bg-white/10 px-5 py-4 backdrop-blur ${accentColor ? accentContrastClassName : "text-white"}`.trim()}>
                        <p className={`text-xs uppercase tracking-wide ${accentColor ? accentSoftTextClassName : "text-white/70"}`.trim()}>Team Members Active</p>
                        <div className="mt-2 flex items-center gap-3">                            
                            {displayedWorkspaceUsers.length > 0 && (
                                <AvatarGroup
                                    size="sm"
                                    max={3}
                                    total={remainingWorkspaceUsers > 0 ? remainingWorkspaceUsers : undefined}
                                    classNames={{
                                        base: "border-white/40 py-1.5 px-3",
                                        count: "bg-white/20 text-white data-[hover=true]:translate-x-0 data-[hover=true]:transition-none"
                                    }}
                                >
                                    {displayedWorkspaceUsers.map(user=>(
                                        <Tooltip key={user.id ?? user._id} content={user.name ?? user.email ?? "User"} placement="bottom" delay={300}>
                                            <Avatar
                                                isBordered
                                                size="sm"
                                                src={user.avatarUrl}
                                                name={user.name}
                                                className="border-white/60 bg-slate-800/70"
                                                imgProps={{ referrerPolicy: "no-referrer" }}
                                            />
                                        </Tooltip>
                                    ))}
                                </AvatarGroup>
                            )}
                        </div>
                        <p className={`text-xs mt-1 ${accentColor ? accentSoftTextClassName : "text-white/70"}`.trim()}>{formatNumber(monthlyStats.teamMembersCount)} in this workspace</p>
                    </div>
                </div>
            </div>
        </section>
        <section className="space-y-4">
            <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Quick Launch</h2>
            <div className={clsx("w-full flex gap-4 oveflow-x-hidden",
                                "[&>*:nth-child(n+3)]:hidden",
                               "[@media(min-width:550px)]:[&>*:nth-child(-n+3)]:!inline-flex [@media(min-width:550px)]:[&>*:nth-child(n+4)]:hidden",
                               "[@media(min-width:725px)]:[&>*:nth-child(-n+4)]:!inline-flex [@media(min-width:725px)]:[&>*:nth-child(n+5)]:hidden",
                               "[@media(min-width:920px)]:[&>*:nth-child(-n+5)]:!inline-flex [@media(min-width:920px)]:[&>*:nth-child(n+6)]:hidden"
                )}>
                {quickLaunchItems.map(({flow, startedInstant})=>{
                    const flowCredits = flow.referenceParameters?.credits
                    const flowVersion =
                        flow?.published?.version
                        ?? (flow?.published ? 1 : null)
                        ?? flow?.replication?.sourceVersion
                        ?? (flow?.replication?.source ? 1 : null)
                        ?? null
                    const lastRunLabel = startedInstant ? startedInstant.toZonedDateTimeISO("UTC").toLocaleString("en-US", {
                        month: "short",
                        day: "numeric"
                    }) : "—"
                    return (
                          <Card 
                            isPressable
                                onPress={()=>handleOpenWorkflowOverview(flow)}
                                style={{"transition-property": "transform, background, shadow"}}
                                key={flow.id} 
                                className="group shrink-0 border-none w-40 gap-4 aspect-video shadow-md hover:shadow-lg transition-shadow duration-300" radius="lg">
                                {flowVersion !== null && flowVersion !== undefined && (
                                    <div className="absolute right-2 top-2 z-20 rounded-full bg-white/95 px-2 py-0.5 text-[11px] font-semibold text-slate-700 shadow-sm ring-1 ring-black/5">
                                        v{flowVersion}
                                    </div>
                                )}
                                <CardHeader 
                                    className="flex-col items-start absolute z-10 bg-black/20 h-full"
                                    >
                                    <div 
                                        style={{ backgroundImage: workspaceGradient}}
                                        className="h-full w-full absolute -m-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"/>                                    
                                </CardHeader>
                                <div 
                                    className="absolute z-10 text-left rounded-xl bg-background"
                                    style={{
                                        width: "calc(100% - 4px)",
                                        height: "calc(100% - 4px)",
                                        margin:"2px"
                                    }}
                                    />
                                    <div className={`z-20 opacity-80 py-5 px-2 place-items-center flex flex-col space-y-2 text-slate-400 hover:${accentTextClassName}`}>
                                        <Icon icon={`lucide:${flow.referenceParameters?.icon}`} className={`w-6 h-6 z-20 transition-colors duration-300`}/>
                                        <span className="font-semibold text-sm text-slate-600 group-hover:text-slate-700 ">{flow.referenceParameters?.short ?? flow.title}</span>
                                    </div>
                        </Card>
                    )
                })}
            </div>
        </section>

        <section className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Recent Activity</h2>
                <div className="flex items-center gap-2">
                    {selectedFlowInstanceIds.size > 0 && (
                        <>
                            <Button
                                variant="flat"
                                color={accentColor ? "default" : "primary"}
                                className={accentColor ? accentTextClassName : undefined}
                                radius="sm"
                                isLoading={isDownloadingPptx}
                                onPress={downloadSelectedOutputs}
                            >
                                Download PPTX ({selectedFlowInstanceIds.size})
                            </Button>
                            <Button
                                variant="light"
                                color="default"
                                radius="sm"
                                onPress={clearSelectedFlowInstances}
                            >
                                Clear
                            </Button>
                        </>
                    )}
                </div>
            </div>
            <div className="w-full flex flex-col bg-white/60 rounded-xl shadow-md min-h-48 p-3">
                <Table
                    enableCopy={false}
                    page={0}
                    pageItems={10}
                    searchBadges={templateSearchBadges}
                    onSortedRowsChange={(ids)=>{flowInstanceTableOrderRef.current = ids}}
                    columns={[
                        {renderType: "react", title: "", field: "selectionControl", width: 52, minWidth: 52},
                        {field: "wId", title: "Id", width: 80},
                        {field: "title", title: "Title", width: 220},
                        {renderType: "pill", title: "Tags", width: 120, field: "tags"},
                        {field: "type", title: "Template", width: 200},
                        {field: "dateRun", title: "Date Run", width: 140},
                        {renderType: "react", title: "Status", field: "progressSection", width: 200},
                        {renderType: "actions", title: "Actions", width: 120, actions: [
                            {title: "View", icon: <EyeIcon/>, action: (d)=>navigate(`/flowinstance/${d.id}`)},
                            {title: "Run", icon: <PlayIcon/>, action: (d)=>MainStore().doPrimitiveAction( d, "continue_flow_instance")},
                            {title: "Config", icon: <Cog8ToothIcon/>, action: (d)=>setEditFlowInstance(MainStore().primitive(d.id))},
                            {title: "Delete", icon: <TrashIcon/>, action: requestDeleteFlowInstance},
                        ]},
                    ]}
                    data={displayedFlowInstanceInfo.map((item)=>{
                        const checked = selectedFlowInstanceIds.has(item.id)
                        return {
                            ...item,
                            wId: `W-${item.plainId}`,
                            selectionControl: (
                                <div
                                    className="flex items-center justify-center w-full"
                                    onClick={(event)=>event.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        aria-label={`Select workflow ${item.plainId ?? item.id}`}
                                        checked={checked}
                                        onChange={(event)=>toggleFlowInstanceSelection(item.id, event.target.checked)}
                                        className="h-4 w-4 cursor-pointer accent-emerald-600"
                                    />
                                </div>
                            )
                        }
                    })}
                    className='w-full min-h-[24em] max-h-inherit text-slate-700'
                />
            </div>
            <div className="fixed -left-[99999px] top-0 h-[900px] w-[1400px] overflow-hidden pointer-events-none opacity-0">
                {exportingFlowInstancePrimitive && (
                    <div key={`flow_export_single_${exportingFlowInstancePrimitive.id}_${exportRenderKey}`} className="h-[900px] w-[1400px]">
                        <FlowInstanceOutput
                            primitive={exportingFlowInstancePrimitive}
                            ref={(refInstance)=>{
                                singleExportFlowOutputRef.current = refInstance
                            }}
                        />
                    </div>
                )}
            </div>
        </section>

        <section className="space-y-4">
            <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Your Workflows</h2>
            {workflowCategories.length === 0 && (
                <div className="mt-4 flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white/60 py-12 text-center text-slate-500">
                    <Icon icon="lucide:sparkles" className="h-8 w-8 text-slate-400" />
                    <p className="mt-3 text-sm">No workflows yet. Import a template to get started.</p>
                </div>
            )}
            {workflowCategories.length > 0 && (
                <div className="space-y-6">
                    {workflowCategories.map((category)=>(
                        <div key={category.key} className="space-y-3">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                                <h3 className="text-sm font-semibold text-slate-700">{category.title}</h3>
                                {category.description && (
                                    <p className="text-xs text-slate-500">{category.description}</p>
                                )}
                            </div>
                            <div className="w-full mt-2 overflow-y-auto pr-1 grid sm:grid-cols-2 gap-4">
                                {category.items.map(({kind, record}, index) => {
                                    const iconName = record?.referenceParameters?.icon
                                    const timeLabel = record?.referenceParameters?.time ?? record?.referenceParameters?.duration
                                    const outputLabel = record?.referenceParameters?.outputs?.summary ?? record?.referenceParameters?.outputs?.summaryText
                                    const creditsLabel = record?.referenceParameters?.credits
                                    const secondaryLabel = record?.plainId
                                        ? `#${record.plainId}`
                                        : kind === "template"
                                            ? "Template"
                                            : undefined
                                    return (
                                        <div
                                            key={`${category.key}-${record?.id ?? record?._id ?? record?.plainId ?? index}`}
                                            style={{"transition-property": "transform, border, shadow"}}
                                            className={`flex cursor-pointer flex-col place-items-start gap-3 p-5 border bg-background rounded-2xl group hover:shadow-lg hover:${accentBorderClassName} duration-300`}
                                            onClick={()=>setSelectedWorkflowOverview(record)}
                                        >
                                            <div className={`rounded-md ${accentPanelClassName} ${accentTextClassName} p-2`}>
                                                <Icon 
                                                    icon={`lucide:${iconName ?? "sparkles"}`} 
                                                    className={classNames(
                                                        "h-6 w-6 shrink-0",
                                                        iconName === "check" ?  "text-slate-500" : accentTextClassName,
                                                    )} />
                                            </div>
                                            <span className="space-x-2 flex place-items-end">
                                                <p className="text-slate-600 font-semibold">{record?.title ?? "Workflow"}</p>
                                                {secondaryLabel && <p className="text-slate-400 text-sm">{secondaryLabel}</p>}
                                            </span>
                                            <p className="text-sm text-slate-600 line-clamp-2">{record?.referenceParameters?.one_line}</p>
                                            <div className="my-3 text-xs text-slate-600 flex space-x-4">
                                                {(timeLabel || kind === "template") && (
                                                    <div className="flex space-x-1 place-items-center">
                                                        <Icon icon="lucide:clock-3" className="w-3 h-3"/>
                                                        <p>{timeLabel ?? "Est. duration"}</p>
                                                    </div>
                                                )}
                                                {outputLabel && (
                                                    <div className="flex space-x-1 place-items-center">
                                                        <Icon icon="lucide:file-text" className="w-3 h-3"/>
                                                        <p>{outputLabel}</p>
                                                    </div>
                                                )}
                                                {creditsLabel !== undefined && creditsLabel !== null && (
                                                    <div className="flex space-x-1 place-items-center">
                                                        <Icon icon="lucide:coins" className="w-3 h-3"/>
                                                        <p>{creditsLabel}</p>
                                                        <p>credits</p>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="my-3 text-xs text-slate-600 flex space-x-4 w-full">
                                                <Button
                                                    color="primary"
                                                    radius="sm"
                                                    className={`h-11 min-w-[140px] justify-center px-5 text-sm font-semibold shadow-sm grow ${accentButtonClassName}`}
                                                    startContent={<Icon icon="lucide:play" className="w-5 h-5" />}
                                                    onPress={(event) => {
                                                        event?.stopPropagation?.()
                                                        handleConfigureWorkflow(record)
                                                    }}
                                                >
                                                    Run Workflow
                                                </Button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
<FlowInstanceEditor
            isOpen={editFlowInstance || showNew}
            flowInstance={editFlowInstance}
            initialWorkflow={preselectedWorkflow}
            onClose={()=>{
                setShowNew(false)
                setEditFlowInstance(undefined)
                setPreselectedWorkflow(undefined)
            }}
        />
        <MultiSetupModal
            flow={multiSetupFlow}
            isOpen={Boolean(multiSetupFlow)}
            onClose={()=>setMultiSetupFlow(undefined)}
            accentButtonClassName={accentButtonClassName}
        />
        {selectedWorkflowOverview && (
            <Popup
                setOpen={handleCloseWorkflowOverview}
                padding="p-0"
                width="max-w-7xl"
            >
                <div className="p-6 pb-10">
                    <WorkflowOverview
                        flow={selectedWorkflowOverview}
                        onRun={handleConfigureWorkflow/*handleRunWorkflow*/}
                        onConfigure={handleConfigureWorkflow}
                        onClose={handleCloseWorkflowOverview}
                    />
                </div>
            </Popup>
        )}
        {flowInstancePendingDelete && (
            <ConfirmationPopup
                title="Confirm deletion"
                message={`Are you sure you want to delete flow instance #${flowInstancePendingDelete.plainId ?? flowInstancePendingDelete.id}${flowInstancePendingDelete.title ? ` (${flowInstancePendingDelete.title})` : ""}?`}
                confirm={confirmDeleteFlowInstance}
                confirmText="Delete"
                confirmColor="danger"
                cancel={()=>setFlowInstancePendingDelete(undefined)}
            />
        )}
    </div> 
    {isWorkspaceLoading && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/45 backdrop-blur-sm">
            <Spinner color="primary" label="Loading workflows" />
        </div>
    )}
    </div>
    )
}
